// frontend/src/services/agent/AgentValidator.ts
/**
 * 🧠 Pensado por Alcélio Gomes - Codificado com Claude Code
 * 🚀 NeuroAgentes Platform - Intelligent Agent Development
 * 
 * AUDITORIA: Validadores para operações de agentes.
 * Centraliza todas as regras de validação e business rules.
 */

import { Agent } from "../../types";
import { commonRules, validate, ValidationRule } from "../../utils/validators";
import { createLogger } from "../../utils/logger";

const logger = createLogger('AgentValidator');

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface AgentValidationContext {
  isCreating: boolean;
  existingAgents?: Agent[];
  projectId?: string;
}

/**
 * Classe responsável pela validação de dados de agentes
 */
export class AgentValidator {
  /**
   * Valida dados de agente para criação/atualização
   */
  static validateAgent(
    agentData: Partial<Agent>, 
    context: AgentValidationContext = { isCreating: false }
  ): ValidationResult {
    logger.debug('Validando dados do agente', { isCreating: context.isCreating });

    const errors: string[] = [];
    const warnings: string[] = [];

    // Validações obrigatórias
    if (context.isCreating || agentData.nome !== undefined) {
      const nameError = validate(agentData.nome, [
        commonRules.required('Nome do agente é obrigatório'),
        this.createMinLengthRule(2, 'Nome deve ter pelo menos 2 caracteres'),
        this.createMaxLengthRule(255, 'Nome não pode exceder 255 caracteres')
      ]);
      if (nameError) errors.push(nameError);
    }

    if (context.isCreating || agentData.descricao !== undefined) {
      const descriptionError = validate(agentData.descricao, [
        commonRules.required('Descrição do agente é obrigatória'),
        this.createMinLengthRule(10, 'Descrição deve ter pelo menos 10 caracteres'),
        this.createMaxLengthRule(1000, 'Descrição não pode exceder 1000 caracteres')
      ]);
      if (descriptionError) errors.push(descriptionError);
    }

    // Validar projeto ID
    if (context.isCreating) {
      if (!context.projectId && !agentData.projetoId) {
        errors.push('ID do projeto é obrigatório');
      }
    }

    // Validar status
    if (agentData.status !== undefined) {
      if (!this.isValidStatus(agentData.status)) {
        errors.push('Status inválido. Use: active, inactive, training');
      }
    }

    // Validar plataforma
    if (agentData.plataforma_id !== undefined) {
      if (!this.isValidPlatform(agentData.plataforma_id)) {
        errors.push('Plataforma inválida. Use: 1 (WhatsApp), 4 (Webhook)');
      }
    }

    // Validar WhatsApp number se fornecido
    if (agentData.configuracao?.telefone !== undefined) {
      const phoneError = this.validateWhatsAppNumber(agentData.configuracao.telefone);
      if (phoneError) errors.push(phoneError);
    }

    // Validar webhook URL se fornecido
    if (agentData.configuracao?.webhook_url !== undefined) {
      const urlError = this.validateWebhookURL(agentData.configuracao.webhook_url);
      if (urlError) errors.push(urlError);
    }

    // Verificar duplicatas de nome
    if (context.existingAgents && agentData.nome) {
      const duplicateName = context.existingAgents.find(
        existing => existing.nome.toLowerCase() === agentData.nome!.toLowerCase() &&
                   existing.id !== agentData.id
      );
      if (duplicateName) {
        warnings.push('Já existe um agente com este nome');
      }
    }

    // Business rules específicas
    if (agentData.plataforma_id === 1 && !agentData.configuracao?.telefone) {
      warnings.push('Agentes WhatsApp devem ter número configurado');
    }

    if (agentData.plataforma_id === 4 && !agentData.configuracao?.webhook_url) {
      warnings.push('Agentes Webhook devem ter URL configurada');
    }

    const result: ValidationResult = {
      isValid: errors.length === 0,
      errors,
      warnings
    };

    if (!result.isValid) {
      logger.warn('Validação falhou', { errors, warnings });
    }

    return result;
  }

  /**
   * Valida se o agente pode ser removido
   */
  static validateDeletion(agent: Agent): ValidationResult {
    logger.debug('Validando remoção do agente', { agentId: agent.id });

    const errors: string[] = [];
    const warnings: string[] = [];

    // Agentes ativos não devem ser removidos
    if (agent.status === 'ativo') {
      warnings.push('Agente está ativo. Considere desativar antes de remover.');
    }

    // Verificar conexão através de metadata se disponível
    // (isConnected não existe na interface Agent, seria obtido via API de status)
    // Por enquanto, apenas aviso baseado no status

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Valida dados para conexão do agente
   */
  static validateConnection(agent: Agent): ValidationResult {
    const errors: string[] = [];

    if (agent.status !== 'ativo') {
      errors.push('Agente deve estar ativo para conectar');
    }

    if (agent.plataforma_id === 1 && !agent.configuracao?.telefone) {
      errors.push('Número WhatsApp é obrigatório para conectar');
    }

    if (agent.plataforma_id === 4 && !agent.configuracao?.webhook_url) {
      errors.push('URL do webhook é obrigatória para conectar');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings: []
    };
  }

  // Métodos auxiliares privados

  private static createMinLengthRule(minLength: number, message: string): ValidationRule {
    return {
      validator: (value) => {
        if (typeof value !== 'string') return false;
        return value.trim().length >= minLength;
      },
      message
    };
  }

  private static createMaxLengthRule(maxLength: number, message: string): ValidationRule {
    return {
      validator: (value) => {
        if (typeof value !== 'string') return false;
        return value.length <= maxLength;
      },
      message
    };
  }

  private static isValidStatus(status: string): boolean {
    return ['active', 'inactive', 'training'].includes(status);
  }

  private static isValidPlatform(platformId: number): boolean {
    return [1, 4].includes(platformId); // 1 = WhatsApp, 4 = Webhook
  }

  private static validateWhatsAppNumber(phone: string): string | null {
    if (!phone || phone.trim() === '') return null;
    
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 10 || cleanPhone.length > 15) {
      return 'Número WhatsApp deve ter entre 10 e 15 dígitos';
    }
    
    return null;
  }

  private static validateWebhookURL(url: string): string | null {
    if (!url || url.trim() === '') return null;
    
    try {
      const parsedURL = new URL(url);
      if (!['http:', 'https:'].includes(parsedURL.protocol)) {
        return 'URL deve usar protocolo HTTP ou HTTPS';
      }
      return null;
    } catch {
      return 'URL do webhook é inválida';
    }
  }
}